import { useEffect, useState } from 'react';
import { FolderKanban, Globe, Lock, Users, UsersRound, User as UserIcon, X } from 'lucide-react';
import type { AccessGrantView } from '@vwt/contracts';
import { Button, cn, Dialog, DialogContent, DialogFooter, Field, Textarea, toast } from '@vwt/ui';
import { useApiMutation, useApiQuery } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';
import { PrincipalPicker } from './common.js';

type Resource = { kind: 'project' | 'work' | 'request'; id: string };

const icons = { USER: UserIcon, TEAM: Users, GROUP: UsersRound, PROJECT: FolderKanban } as const;

/** Explicit, previewed visibility change (Section 4.3/4.4). */
export function VisibilityDialog({ open, onOpenChange, resource }: { open: boolean; onOpenChange: (open: boolean) => void; resource: Resource }) {
  const { t } = useTranslation();
  const getOp = resource.kind === 'project' ? 'getProjectVisibility' : resource.kind === 'work' ? 'getWorkVisibility' : 'getRequestVisibility';
  const setOp = resource.kind === 'project' ? 'setProjectVisibility' : resource.kind === 'work' ? 'setWorkVisibility' : 'setRequestVisibility';
  const current = useApiQuery(getOp, { params: { id: resource.id } }, { enabled: open });
  const save = useApiMutation(setOp, { invalidate: [getOp, 'getWorkItem', 'getRequest', 'getProject', 'listWorkItems', 'listRequests', 'listProjects'] });
  const [visibility, setVisibility] = useState<'COMPANY' | 'RESTRICTED'>('COMPANY');
  const [grants, setGrants] = useState<AccessGrantView[]>([]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (current.data) {
      setVisibility(current.data.data.visibility);
      setGrants(current.data.data.grants);
    }
  }, [current.data]);

  const submit = async () => {
    try {
      await save.mutateAsync({
        params: { id: resource.id },
        body: { expectedVersion: current.data!.data.version, visibility, grants: grants.map((g) => ({ principalKind: g.principalKind, principalId: g.principalId })), ...(reason ? { reason } : {}) },
      });
      toast.success(t('visibility.changed'));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('visibility.title')} description={t('visibility.inherit')}>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('visibility.title')}>
            {(['COMPANY', 'RESTRICTED'] as const).map((v) => {
              const Icon = v === 'COMPANY' ? Globe : Lock;
              return (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={visibility === v}
                  onClick={() => setVisibility(v)}
                  className={cn('flex flex-col items-start gap-1 rounded-[var(--radius)] border p-3 text-left', visibility === v ? 'border-accent bg-accent-soft/40' : 'border-border hover:bg-muted')}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Icon className="size-4" /> {t(`status.visibility.${v}`)}
                  </span>
                  <span className="text-xs text-fg-muted">{t(`status.visibilityHelp.${v}`)}</span>
                </button>
              );
            })}
          </div>
          {visibility === 'RESTRICTED' ? (
            <Field label={t('visibility.grants')}>
              <ul className="flex flex-col gap-1">
                {grants.map((g) => {
                  const Icon = icons[g.principalKind];
                  return (
                    <li key={`${g.principalKind}:${g.principalId}`} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm">
                      <Icon className="size-4 text-fg-subtle" /> <span className="flex-1 truncate">{g.principalName}</span>
                      <button aria-label={t('common.delete')} onClick={() => setGrants((list) => list.filter((x) => x !== g))} className="text-fg-subtle hover:text-danger">
                        <X className="size-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
              <PrincipalPicker
                label={t('visibility.addGrant')}
                kinds="USER,TEAM,GROUP"
                value={null}
                placeholder={t('visibility.addGrant')}
                allowClear={false}
                onChange={(p) => p && setGrants((list) => (list.some((g) => g.principalId === p.id) ? list : [...list, { principalKind: p.kind, principalId: p.id, principalName: p.name }]))}
              />
            </Field>
          ) : null}
          <Field label={t('common.reason')} hint={t('common.optional')}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void submit()} loading={save.isPending} disabled={!current.data}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
